import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Outlet, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { GameHome } from '../Games';
import ShareButton from '../../components/ShareButton';
import GameModeSwitcher from '../../components/GameModeSwitcher';
import SeasonTeamsPage from './SeasonTeamsPage';
import SeasonShotsPage from './SeasonShotsPage';
import PlayersPage from './PlayersPage';
import PlayerStatSheetPage from './PlayerStatSheetPage';
import DetailsPage from './DetailsPage';
import RosterPage from './RosterPage';
import SeasonKPITargetsPage from './SeasonKPITargetsPage';
import GamesListPage from './GamesListPage';
import { sessionManager } from '../../config/sessionManager';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { formatLocalDate } from '../../utils/dateUtils';
import { GAME_MODE_PRACTICE, GAME_MODE_REGULAR, GameMode, parseGameMode } from '../../utils/seasonType';
interface SeasonHeaderProps {
  gamesMode: GameMode;
  onGamesModeChange: (mode: GameMode) => void;
}

// Unified responsive season header (mobile + web)
const SeasonHeader: React.FC<SeasonHeaderProps> = ({ gamesMode, onGamesModeChange }) => {
  const { seasonId } = useParams();
  const location = useLocation();
  const basePath = seasonId ? `/seasons/${seasonId}` : '/home';
  const seasonName = sessionManager.getSeasonName() || 'Season';
  const clientName = sessionManager.getClientName();
  const startDateRaw = sessionManager.getStartDate();
  const endDateRaw = sessionManager.getEndDate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const [logoUrl, setLogoUrl] = useState<string>('');
  const [recordText, setRecordText] = useState<string>('');
  const [showModeSwitcher, setShowModeSwitcher] = useState(false);

  function formatDate(dateStr?: string) {
    if (!dateStr) return '';
    return formatLocalDate(dateStr, undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  }
  const dateRange =
    startDateRaw && endDateRaw ? `${formatDate(startDateRaw)} - ${formatDate(endDateRaw)}` : '12/13/2024 - 5/31/2025';

  const tabs = [
    {
      to: `${basePath}/details`,
      label: 'Details',
      active: (p: string) => p.endsWith('/details'),
    },
    {
      to: `${basePath}/roster`,
      label: 'Roster',
      active: (p: string) => p.endsWith('/roster'),
    },
    {
      to: `${basePath}/kpi`,
      label: 'KPIs',
      active: (p: string) => p.endsWith('/kpi'),
    },
    {
      to: `${basePath}/games`,
      label: 'Games',
      active: (p: string) => p.includes('/games'),
    },
    {
      to: `${basePath}/teams`,
      label: 'Teams',
      active: (p: string) => p.endsWith('/teams'),
    },
    {
      to: `${basePath}/shots`,
      label: 'Shots',
      active: (p: string) => p.endsWith('/shots'),
    },
    {
      to: `${basePath}/players`,
      label: 'Players',
      active: (p: string) => p.includes('/players'),
    },
  ];

  const recalc = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 5);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };
  useEffect(() => {
    recalc();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', recalc, { passive: true });
    window.addEventListener('resize', recalc);
    return () => {
      el.removeEventListener('scroll', recalc);
      window.removeEventListener('resize', recalc);
    };
  }, []);

  useEffect(() => {
    const clientId = sessionManager.getClientId();
    if (!clientId) return;

    const fetchClient = async () => {
      try {
        const res = await api.get(`${API_URLS.GET_CLIENT_BY_ID}`, {
          params: { clientId },
        });
        if (res.data?.success) {
          const data = res.data.data || {};
          const img: string | undefined = data.image;
          const fullPath = img ? `${process.env.REACT_APP_UPLOAD_URL}uploads/clients/${img}` : '';
          setLogoUrl(fullPath);
        }
      } catch {
        // keep default logo
      }
    };

    const fetchSeasonMeta = async () => {
      if (!seasonId) return;
      try {
        const res = await api.get(`${API_URLS.GET_SEASON_BY_ID}/${seasonId}`, {
          params: { clientId },
        });
        if (res.data?.success) {
          const season = res.data.data || {};
          setShowModeSwitcher(!season.isTournament && sessionManager.canAccessPracticeGames());
        } else {
          setShowModeSwitcher(sessionManager.canAccessPracticeGames());
        }
      } catch {
        setShowModeSwitcher(sessionManager.canAccessPracticeGames());
      }
    };

    const fetchSeasonRecord = async () => {
      if (!seasonId) return;
      try {
        const res = await api.get(`${API_URLS.GET_SEASON_CHART_SERIES}`, {
          params: { clientId, seasonId, gameMode: gamesMode },
        });
        if (res.data?.success) {
          const games = res.data.data?.games || [];
          const wins = games.filter((g: any) => g.outcome === 'win').length;
          const losses = games.filter((g: any) => g.outcome === 'loss').length;
          const ties = games.filter((g: any) => g.outcome === 'tie').length;
          const record = ties > 0 ? `${wins}W – ${losses}L – ${ties}T` : `${wins}W – ${losses}L`;
          setRecordText(record);
        }
      } catch {
        setRecordText('');
      }
    };

    fetchClient();
    fetchSeasonMeta();
    fetchSeasonRecord();
  }, [seasonId, gamesMode]);

  const scrollTabs = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  return (
    <div className="w-full bg-[#19133c] px-4 pt-4 pb-3 flex flex-col gap-4">
      <div className="w-full grid grid-cols-2 md:grid-cols-3 items-center gap-3">
        <div className="flex flex-col items-start min-w-0">
          <div className="relative flex flex-row items-center gap-2 min-w-0">
            <span className="text-lg md:text-3xl font-bold text-white leading-tight truncate">{seasonName}</span>
            <ShareButton
              contentType="season"
              resourceId={seasonId || ''}
              variant="icon"
              className="w-5 md:w-6 h-5 md:h-6 shrink-0"
            />
          </div>
          <span className="text-xs md:text-lg text-white truncate max-w-full">{clientName}</span>
        </div>

        <div className="hidden md:flex flex-col items-center justify-center gap-2">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Team Logo"
              className="min-w-14 min-h-14 w-14 h-14 sm:w-20 sm:h-20 rounded-full border-2 border-white bg-white object-contain"
            />
          )}
          {showModeSwitcher && (
            <GameModeSwitcher
              mode={gamesMode}
              onChange={onGamesModeChange}
              variant="dark"
              className="mt-1"
            />
          )}
        </div>

        <div className="flex flex-col items-end min-w-0">
          <span className="text-lg md:text-3xl font-bold text-white whitespace-nowrap">{recordText || ''}</span>
          <span className="text-[10px] md:text-lg text-white mt-1 whitespace-nowrap">{dateRange}</span>
        </div>
      </div>

      {showModeSwitcher && (
        <div className="flex md:hidden justify-center">
          <GameModeSwitcher mode={gamesMode} onChange={onGamesModeChange} variant="dark" />
        </div>
      )}

      <div className="relative w-full">
        {showLeft && (
          <div
            aria-label="Scroll left"
            onClick={() => scrollTabs(-1)}
            className="absolute top-0 bottom-0 -left-1 w-8 flex items-center justify-start pl-1 md:hidden z-30 cursor-pointer select-none bg-gradient-to-r from-[#19133c] via-[#19133c]/70 to-transparent"
          >
            <span className="text-white text-3xl leading-none drop-shadow -ml-1">‹</span>
          </div>
        )}
        {showRight && (
          <div
            aria-label="Scroll right"
            onClick={() => scrollTabs(1)}
            className="absolute top-0 bottom-0 -right-1 w-8 flex items-center justify-end pr-1 md:hidden z-30 cursor-pointer select-none bg-gradient-to-l from-[#19133c] via-[#19133c]/70 to-transparent"
          >
            <span className="text-white text-3xl leading-none drop-shadow mr-0">›</span>
          </div>
        )}
        <div ref={scrollRef} className="overflow-x-auto md:overflow-visible scrollbar-hide relative z-10">
          <div
            className={`flex md:grid gap-2 py-2 pr-4 md:pr-0 min-w-max md:min-w-full ${
              tabs.length > 6 ? 'md:grid-cols-7' : 'md:grid-cols-6'
            }`}
          >
            {tabs.map((t) => (
              <NavLink key={t.label} to={t.to}>
                {({ isActive }) => {
                  const active = isActive || t.active(location.pathname);
                  return (
                    <div
                      className={`relative flex-none md:flex-1 w-28 md:w-full px-2 md:px-3 pt-2 pb-3 rounded-md text-sm font-semibold transition-all text-center ${
                        active ? 'bg-[#7fe526] text-black' : 'bg-[#255393] text-white'
                      }`}
                    >
                      <span>{t.label}</span>
                      {active && (
                        <span
                          className="md:hidden absolute left-1/2 -bottom-2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent"
                          style={{ borderTopColor: '#7fe526' }}
                        />
                      )}
                    </div>
                  );
                }}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const canAccessPracticeGames = sessionManager.isEmployee() || sessionManager.canAccessPracticeGames();
  const [gamesMode, setGamesMode] = useState<GameMode>(() => {
    const stored = parseGameMode(sessionManager.getGamesMode());
    if (stored === GAME_MODE_PRACTICE && !(sessionManager.isEmployee() || sessionManager.canAccessPracticeGames())) {
      sessionManager.setGamesMode(GAME_MODE_REGULAR);
      return GAME_MODE_REGULAR;
    }
    return stored;
  });

  const handleGamesModeChange = (mode: GameMode) => {
    if (!canAccessPracticeGames && mode === GAME_MODE_PRACTICE) return;
    if (mode === gamesMode) return;
    sessionManager.setGamesMode(mode);
    setGamesMode(mode);
  };

  return (
    <div className="w-full">
      <SeasonHeader gamesMode={gamesMode} onGamesModeChange={handleGamesModeChange} />
      <main className="w-full rounded-b flex-1 flex flex-col items-center relative z-60">
        <Outlet key={gamesMode} />
      </main>
    </div>
  );
};

const HomeRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Home />}>
        <Route index element={<Navigate to="teams" replace />} />
        <Route path="details" element={<DetailsPage />} />
        <Route path="roster" element={<RosterPage />} />
        <Route path="kpi" element={<SeasonKPITargetsPage />} />
        <Route path="teams" element={<SeasonTeamsPage />} />
        <Route path="shots" element={<SeasonShotsPage />} />
        <Route path="players" element={<PlayersPage />} />
        <Route path="players/:playerSeasonId" element={<PlayerStatSheetPage />} />
        <Route path="games" element={<GamesListPage />} />
        <Route path="games/:gameId/*" element={<GameHome />} />
      </Route>
    </Routes>
  );
};

export default HomeRoutes;
